import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AuthService } from '@shared/services/auth.service';
import { PortalRole, RoleContextService } from '@shared/services/role-context.service';
import { ThemeService } from '@shared/services/theme/theme.service';
import { UserApiServiceCollection } from 'app/features/user/services/api-service/api.service.component';
import { Subject, catchError, of, switchMap, takeUntil } from 'rxjs';
import { Tooltip } from 'primeng/tooltip';
import { DataService } from '../../services/data/data.service';

@Component({
  selector: 'app-usermenu',
  templateUrl: './usermenu.component.html',
  styleUrls: ['./usermenu.component.scss'],
  standalone: true,
  imports: [Tooltip],
})
export class UsermenuComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private profilePictureObjectUrl: string | null = null;
  avatar: boolean;
  pic: any;
  username = '';
  email = '';
  activeCostCenterName = '';
  activeCostCenterTitle = 'Active Cost Center:';
  activeRole: PortalRole | null = null;
  activeCostCenterPlaceholder = 'No active cost center';

  constructor(
    private auth: AuthService,
    private apiService: UserApiServiceCollection,
    private sanitizer: DomSanitizer,
    public dataService: DataService,
    public themeService: ThemeService,
    private router: Router,
    private roleContextService: RoleContextService,
  ) {}

  ngOnInit(): void {
    this.subscribeToLanguageChanges();

    const texts = this.dataService.getTexts();
    this.activeCostCenterTitle = texts.ActiveCostCenterTitle;
    this.activeCostCenterPlaceholder = texts.ActiveCostCenterPlaceholder;
    this.activeCostCenterName = this.activeCostCenterPlaceholder;
    this.auth.getUser();
    this.getAadProfilePicture();
    this.loadUserData();
    this.subscribeToRoleContextAndActiveCostCenterName();
  }

  ngOnDestroy(): void {
    this.revokeProfilePictureObjectUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private revokeProfilePictureObjectUrl(): void {
    if (this.profilePictureObjectUrl) {
      URL.revokeObjectURL(this.profilePictureObjectUrl);
      this.profilePictureObjectUrl = null;
    }
  }

  clearProfilePicture(): void {
    this.revokeProfilePictureObjectUrl();
    this.avatar = false;
    this.pic = null;
  }

  private subscribeToLanguageChanges(): void {
    this.dataService.languageChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const previousPlaceholder = this.activeCostCenterPlaceholder;
      const texts = this.dataService.getTexts();
      this.activeCostCenterTitle = texts.ActiveCostCenterTitle;
      this.activeCostCenterPlaceholder = texts.ActiveCostCenterPlaceholder;

      if (this.activeCostCenterName === previousPlaceholder) {
        this.activeCostCenterName = this.activeCostCenterPlaceholder;
      }
    });
  }

  private loadUserData(): void {
    this.apiService
      .getCurrentUserData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.username = data.name;
          this.email = data.email;
        },
        error: () => {
          this.username = '';
          this.email = '';
        },
      });
  }

  private subscribeToRoleContextAndActiveCostCenterName(): void {
    this.roleContextService.activeRole$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((role) => {
          this.activeRole = role;

          const availableRoles = this.roleContextService.getAvailableRoles();
          const hasUserRole = availableRoles.includes('user');
          if (!hasUserRole) {
            return of([]);
          }

          return this.apiService.getDepartment().pipe(catchError(() => of([])));
        }),
      )
      .subscribe((departments) => {
        const activeDepartment = departments?.find((department) => department.isActiveCostBooking);
        if (activeDepartment?.name) {
          this.activeCostCenterName = activeDepartment.name;
          return;
        }

        this.activeCostCenterName = this.activeCostCenterPlaceholder;
      });
  }

  private getAadProfilePicture(): void {
    this.auth
      .getAadProfilePicture()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (image: any) => {
          this.clearProfilePicture();
          if (image != null) {
            const objectURL = URL.createObjectURL(image);
            this.profilePictureObjectUrl = objectURL;
            this.pic = this.sanitizer.bypassSecurityTrustUrl(objectURL);
            this.avatar = true;
          }
        },
        error: () => {
          this.clearProfilePicture();
        },
      });
  }

  navigateToSettings(): void {
    if (this.activeRole === 'admin') {
      this.router.navigateByUrl('/admin/user-settings');
      return;
    }

    if (this.activeRole === 'instanceadmin') {
      this.router.navigateByUrl('/in/user-settings');
      return;
    }

    this.router.navigateByUrl('/user/user-settings');
  }

  get activeCostCenterTooltip(): string {
    return `${this.activeCostCenterTitle} ${this.activeCostCenterName}`;
  }

  get showActiveCostCenterIndicator(): boolean {
    return this.activeCostCenterName !== this.activeCostCenterPlaceholder;
  }
}
